import React, { useLayoutEffect, useRef, useState } from 'react';
import './CoachAvatar.css';
import {
  isCoachAvatarImageLoaded,
  markCoachAvatarImageLoaded,
  subscribeCoachAvatarImageLoaded,
} from './coachAvatarImageResource';

/**
 * Coach identity renderer. A configured emoji is a valid settled identity;
 * the generic loading gap is not. Portraits stay mounted while decoding so
 * the browser can use its tiny cached variant without flashing a placeholder.
 */
const CoachAvatar = ({
  imageUrl = null,
  emoji = '🥋',
  identitySettled = false,
  alt = '',
  eager = false,
  className = '',
  imageClassName = '',
  emojiClassName = '',
}) => {
  const [imageState, setImageState] = useState(() => ({
    url: imageUrl,
    loaded: isCoachAvatarImageLoaded(imageUrl),
    failed: false,
  }));
  const imageRef = useRef(null);
  const stateMatchesUrl = imageState.url === imageUrl;
  const loaded = stateMatchesUrl ? imageState.loaded : isCoachAvatarImageLoaded(imageUrl);
  const failed = stateMatchesUrl ? imageState.failed : false;

  useLayoutEffect(() => {
    const url = imageUrl;
    const cached = isCoachAvatarImageLoaded(url);
    setImageState(previous => (
      previous.url === url && previous.loaded === cached && previous.failed === false
        ? previous
        : { url, loaded: cached, failed: false }
    ));
    const unsubscribe = subscribeCoachAvatarImageLoaded(url, () => {
      setImageState(previous => {
        if (previous.url === url && previous.failed) return previous;
        return { url, loaded: true, failed: false };
      });
    });
    const image = imageRef.current;
    if (url && image?.complete && image.naturalWidth > 0) {
      setImageState({ url, loaded: true, failed: false });
      markCoachAvatarImageLoaded(url);
    }
    return unsubscribe;
  }, [imageUrl]);

  const handleLoad = () => {
    setImageState({ url: imageUrl, loaded: true, failed: false });
    markCoachAvatarImageLoaded(imageUrl);
  };

  const handleError = () => {
    setImageState(previous => (
      previous.url === imageUrl
        ? { ...previous, failed: true }
        : { url: imageUrl, loaded: false, failed: true }
    ));
  };

  const hasImage = Boolean(imageUrl);
  const showImage = hasImage && !failed;
  const showLoadingCue = (hasImage && !loaded) || failed || (!hasImage && !identitySettled);
  const showSettledEmoji = !hasImage && identitySettled;

  return (
    <span className={`coach-avatar-visual${className ? ` ${className}` : ''}`}>
      {showImage && (
        <img
          ref={imageRef}
          src={imageUrl}
          alt={alt}
          className={`coach-avatar-visual-image${loaded ? ' is-loaded' : ''}${imageClassName ? ` ${imageClassName}` : ''}`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          {...(eager ? { fetchpriority: 'high' } : {})}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {showLoadingCue && <span className="workout-coach-avatar-loading-cue" aria-hidden="true" />}
      {showSettledEmoji && (
        <span
          className={`coach-avatar-settled-emoji${emojiClassName ? ` ${emojiClassName}` : ''}`}
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
          aria-hidden={alt ? undefined : 'true'}
        >
          {emoji}
        </span>
      )}
    </span>
  );
};

export default CoachAvatar;
